"use client";

import * as React from "react";
import { Search } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = ["Todos", "Perfiles", "Láminas", "Placas", "Tubería", "Herrajes"];
function CatalogConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="h-96 rounded-lg bg-card animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground font-default">
        Error al cargar productos.{" "}
        <Button onClick={refetch} variant="link" className="p-0 h-auto underline text-primary">
          Reintentar
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-muted-foreground font-default text-lg">
        No hay productos disponibles en este momento.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {posts.map((post, i) => <article data-index={i} key={post.id} className="group rounded-lg bg-card overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:ring-1 hover:ring-primary/40 hover:shadow-lg">
          <Link to={post.link || "#"} className="block">
            <div className="aspect-square overflow-hidden bg-muted">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.customFields?.nombre || post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="h-full w-full bg-muted" />}
            </div>
          </Link>

          <div className="p-5 space-y-3">
            {post.categories && post.categories.length > 0 && <span className="inline-block font-mono text-[11px] uppercase tracking-widest text-secondary">
                {post.categories[0].name}
              </span>}

            <Link to={post.link || "#"} className="block">
              <h3 className="font-default font-bold text-[22px] leading-tight text-foreground">
                {post.customFields?.nombre || post.title}
              </h3>
            </Link>

            {post.customFields?.descripcion && <div className="text-sm text-muted-foreground line-clamp-2 [&_p]:mb-0 font-default" dangerouslySetInnerHTML={{
          __html: post.customFields.descripcion
        }} />}

            <div className="pt-3">
              <Link to={post.link || "#"}>
                <Button variant="outline" size="sm" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  Ver Detalles
                </Button>
              </Link>
            </div>
          </div>
        </article>)}
    </div>;
}
export default function SubProductsGrid() {
  const [search, setSearch] = React.useState("");
  const [activeCategory, setActiveCategory] = React.useState("Todos");
  const [searchQuery] = useWPQueryForSearch(search);
  const wp_query = React.useMemo(() => {
    if (search.trim()) {
      return searchQuery;
    }
    return new WP_Query({
      post_type: "subproducto",
      per_page: 8,
      orderby: "date",
      order: "desc",
      meta_query: [{
        key: "product_category",
        value: "PERFILERIA",
        compare: "="
      }]
    });
  }, [search, searchQuery]);
  return <section data-section-id="2260" id="sub-products-grid" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10">
          <div className="max-w-xl">
            <h2 className="font-default font-bold text-5xl sm:text-6xl lg:text-[72px] leading-[1.05] text-foreground tracking-tighter">
              Nuestro Catálogo.
            </h2>
            <p className="mt-4 text-base text-muted-foreground font-default">
              Soluciones integrales en acero: perfiles estructurales, láminas y herrajes de alta resistencia.
            </p>
          </div>

          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              <ProductsSearch value={search} onChange={setSearch} placeholder="Buscar en el catálogo" className="[&_input]:pl-11 [&_input]:h-12 [&_input]:rounded-md [&_input]:border-border [&_input]:bg-card [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground font-default" />
            </div>
            <CartIcon variant="ghost" className="size-12 rounded-md border border-border hover:bg-muted flex items-center justify-center text-primary" iconSize="size-5" />
          </div>
        </div>

        <div className="mb-12 -mx-4 px-4 overflow-x-auto">
          <div className="flex items-center gap-3 min-w-max pb-2">
            {categories.map((cat, i) => {
            const isActive = activeCategory === cat;
            return <Button key={cat} data-index={i} variant="ghost" size="sm" onClick={() => setActiveCategory(cat)} className={isActive ? "rounded-md px-5 h-10 font-default font-semibold uppercase text-xs tracking-wider bg-primary text-primary-foreground hover:bg-primary-600" : "rounded-md px-5 h-10 font-default font-semibold uppercase text-xs tracking-wider border border-border text-foreground hover:bg-muted"}>
                  {cat}
                </Button>;
          })}
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <CatalogConsumer />
        </PostsProvider>
      </div>
    </section>;
}